/**
 * The reconciliation pass: everything the pile can settle without AI, over
 * the whole company at once.
 *
 * Reading a document costs an AI call and happens once (`extraction` on the
 * item). Everything after it — memory, the pairing of receipts with bank
 * rows, invoices with payments, credit notes with purchases, duplicates and
 * triage — is a pure function of what is stored, and this module recomputes
 * it whenever new facts arrive: after a read, an import, an answer, a
 * booking. So a receipt read last week pairs with a payment imported today,
 * a payment imported today settles an invoice booked last month, and the
 * same evidence supplied in any order ends in the same state.
 *
 * Allocation is global: open bank rows form one pool, and every receipt's
 * candidates are ranked together before any row is assigned, best pair first
 * and ties to the older item. Two receipts therefore never hold the same row,
 * an invoice cannot be settled twice by two rows in one pass, and a row a user
 * has already answered is never taken from them behind their back. What the
 * user decided (`decided`, `rejectedMatches`) is the one thing the pass never
 * rebuilds: decisions are overlaid on the rebuilt proposal, rejected
 * candidates are left out of the ranking.
 *
 * The pass writes only inbox items and bank-row links. It never books.
 */
import { db as defaultDb, type BookkeepingDB } from '@/db/db';
import { AUTO_LINK_MIN_GAP, AUTO_LINK_MIN_SCORE, rankBankRowsForReceipt } from '../bank/match';
import type { RowCandidate } from '../bank/match';
import { deriveOpenItems, foreignCurrencyIdsFrom, refundablePurchases } from '../bank/open-items';
import { merchantKeyOf } from '../merchants/normalize';
import type { Ore } from '../money';
import type { BankRow, InboxFlag, InboxItem, InboxStatus, IsoDate, MerchantRule, Proposal, ProposalAllocation, ReceiptExtraction, Uuid, Verifikation } from '../types';
import type { BankRowClassificationResult, ProcessedItem, ReceiptReadResult } from './internal-types';
import {
  BANK_ROW_MISSING,
  bankSourcesFor,
  directionOf,
  documentedIdsOf,
  ensureBankRowItems,
  isDecided,
  linkRow,
  mergeBankRow,
  mergeReceipt,
  patchItem,
  retireBankRowItems,
  todayIso,
  type CompanyContext,
  type ItemPatch,
} from './pipeline';
import type { OpenItem } from './types';

export interface ReconcileOptions {
  /**
   * Items the calling run has marked `processing` and owns; they are
   * reconciled like open ones. Anything else in `processing` belongs to a run
   * elsewhere and is left alone.
   */
  ownedIds?: ReadonlySet<Uuid> | undefined;
  /**
   * The caller will classify bank rows with AI next: rows whose rule steps
   * found nothing stay `processing` (with their fallback proposal) instead of
   * flashing a question the classification is about to answer.
   */
  holdForClassification?: boolean | undefined;
  today?: IsoDate | undefined;
}

export interface ReconcileResult {
  /** Items whose stored state changed. */
  changed: number;
  /** Bank-row items whose rule steps found nothing; an AI classification would help. */
  needsClassification: Uuid[];
  /** Receipts that turned out to be the document of a purchase booked without one; the caller files them. */
  linkedDocuments: { itemId: Uuid; verifikationId: Uuid }[];
}

/** Statuses a reconciliation may rewrite: not terminal, not failed, not (someone else's) in flight. */
const WORKABLE: ReadonlySet<InboxStatus> = new Set<InboxStatus>(['new', 'ready', 'check', 'needs_you']);

/** Flags triage derives on every pass; not part of what the reader said. */
const DERIVED_FLAGS: ReadonlySet<InboxFlag> = new Set<InboxFlag>(['duplicate', 'locked_period', 'outside_fiscal_year', 'large_amount']);

function byCreated(a: Pick<InboxItem, 'createdAt' | 'id'>, b: Pick<InboxItem, 'createdAt' | 'id'>): number {
  return a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
}

function byRowDate(a: BankRow, b: BankRow): number {
  return a.bookingDate.localeCompare(b.bookingDate) || a.id.localeCompare(b.id);
}

/** Key order must not count as a change, or every pass would bump `updatedAt` and reset an open card. */
function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const v = (value as Record<string, unknown>)[key];
      if (v !== undefined) out[key] = stable(v);
    }
    return out;
  }
  return value;
}

export /** Rows a person could not tell apart either. */
function interchangeable(a: BankRow, b: BankRow): boolean {
  return a.amountOre === b.amountOre && a.bookingDate === b.bookingDate && a.paymentSourceId === b.paymentSourceId;
}

function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(stable(a)) === JSON.stringify(stable(b));
}

/**
 * The reader's result for a receipt: the stored extraction, or — for an item
 * read before extractions were stored — the proposal it was given then, with
 * the flags triage adds stripped off again.
 */
export function extractionOf(item: InboxItem): ReceiptExtraction | undefined {
  if (item.extraction) return item.extraction;
  if (!item.reading || !item.proposal) return undefined;
  const p = item.proposal;
  return {
    at: item.updatedAt,
    proposal: { kind: p.kind, categoryId: p.categoryId, vat: p.vat, confidence: p.confidence, ...(p.question ? { question: p.question } : {}) },
    flags: item.flags.filter((f) => !DERIVED_FLAGS.has(f)),
  };
}

function readResultOf(item: InboxItem, extraction: ReceiptExtraction): ReceiptReadResult {
  return { reading: item.reading!, proposal: extraction.proposal, flags: extraction.flags };
}

function classificationOf(item: InboxItem): BankRowClassificationResult | undefined {
  const c = item.classification;
  if (!c) return undefined;
  return { index: 0, kind: c.kind, categoryId: c.categoryId, vat: c.vat, confidence: c.confidence, counterparty: c.counterparty, needsReceipt: c.needsReceipt };
}

/** What a receipt is matched on: the user's figures first, then the reading's. */
function matchBasis(item: InboxItem): { amountOre: Ore; date: IsoDate; counterparty: string; kind: Proposal['kind']; paymentSourceId: Uuid | null } | null {
  const p = item.proposal;
  const r = item.reading;
  const amountOre = (isDecided(item, 'amountOre') ? p?.amountOre : null) ?? r?.totalOre ?? p?.amountOre ?? null;
  const date = (isDecided(item, 'date') ? p?.date : null) ?? r?.date ?? p?.date ?? null;
  if (amountOre === null || amountOre <= 0 || date === null) return null;
  return {
    amountOre,
    date,
    counterparty: (isDecided(item, 'counterparty') ? p?.counterparty : null) ?? r?.merchant ?? p?.counterparty ?? '',
    kind: (isDecided(item, 'kind') ? p?.kind : null) ?? item.extraction?.proposal.kind ?? p?.kind ?? 'purchase',
    paymentSourceId: (isDecided(item, 'paymentSourceId') ? p?.paymentSourceId : null) ?? null,
  };
}

/** The diff between what an item holds and what the merge produced; empty when nothing changed. */
function diffProcessed(item: InboxItem, processed: ProcessedItem, extra: ItemPatch = {}): ItemPatch {
  const patch: ItemPatch = {};
  const take = <K extends keyof ProcessedItem & keyof InboxItem>(key: K) => {
    const next = processed[key] as InboxItem[K] | undefined;
    if (!sameJson(next, item[key])) (patch as Record<string, unknown>)[key] = next;
  };
  take('status');
  take('tier');
  take('tierReasons');
  take('flags');
  take('proposal');
  take('matchedBankRowIds');
  take('duplicateOfVerifikationId');
  take('duplicateOfItemId');
  take('verifikationId');
  for (const [k, v] of Object.entries(extra)) if (!sameJson(v, item[k as keyof InboxItem])) (patch as Record<string, unknown>)[k] = v;
  return patch;
}

/**
 * Consume `allocations` from the open items: what an earlier row of this pass
 * already claimed is not offered again. An item paid in full disappears.
 */
function consume(openItems: OpenItem[], allocations: readonly ProposalAllocation[]): OpenItem[] {
  return openItems
    .map((o) => {
      const a = allocations.find((x) => x.verifikationId === o.verifikationId);
      if (!a) return o;
      const remaining = Math.abs(o.amountOre) - a.amountOre;
      return { ...o, amountOre: Math.sign(o.amountOre) * Math.max(0, remaining) };
    })
    .filter((o) => o.amountOre !== 0);
}

export async function reconcileCompany(companyId: Uuid, ctx: CompanyContext, db: BookkeepingDB = defaultDb, opts: ReconcileOptions = {}): Promise<ReconcileResult> {
  const today = opts.today ?? todayIso();
  const owned = opts.ownedIds ?? new Set<Uuid>();
  const result: ReconcileResult = { changed: 0, needsClassification: [], linkedDocuments: [] };
  const [items, rows, verifikationer, rules] = await Promise.all([
    db.inboxItems.where('companyId').equals(companyId).toArray(),
    db.bankRows.where('companyId').equals(companyId).toArray(),
    db.verifikationer.where('companyId').equals(companyId).toArray(),
    db.merchantRules.where('companyId').equals(companyId).toArray(),
  ]);
  const ruleByKey = new Map<string, MerchantRule>(rules.map((r) => [r.merchantKey, r]));
  const rowById = new Map(rows.map((r) => [r.id, r]));
  const verifikationById = new Map<Uuid, Verifikation>(verifikationer.map((v) => [v.id, v]));
  const workable = (i: InboxItem) => owned.has(i.id) || (WORKABLE.has(i.status) && i.status !== 'new') || (i.status === 'new' && i.kind === 'bank_row') || (i.status === 'new' && !!extractionOf(i));

  const write = async (item: InboxItem, patch: ItemPatch) => {
    if (!Object.keys(patch).length) return;
    await patchItem(db, item.id, patch);
    result.changed++;
  };

  // ---- Receipts: one pool of open rows, allocated across the collection ---------

  const receipts = items.filter((i) => i.kind === 'receipt' && workable(i) && !!extractionOf(i)).sort(byCreated);
  const bankItemByRow = new Map<Uuid, InboxItem>();
  for (const i of items) if (i.kind === 'bank_row' && i.bankRowId && i.status !== 'skipped' && i.status !== 'private') bankItemByRow.set(i.bankRowId, i);

  // Rows already held: confirmed matches, and matches of receipts this pass does not touch.
  const reserved = new Map<Uuid, Uuid>();
  const receiptIds = new Set(receipts.map((r) => r.id));
  for (const i of items) {
    if (i.kind !== 'receipt' || i.status === 'skipped' || i.status === 'private') continue;
    const confirmed = isDecided(i, 'match') || !receiptIds.has(i.id);
    if (confirmed) for (const id of i.matchedBankRowIds) reserved.set(id, i.id);
  }
  const openRows = rows.filter((r) => r.status === 'open');
  const pool = openRows.filter((r) => !reserved.has(r.id));
  // A row the user has answered on is not reassigned behind their back; a receipt may still ask about it.
  const answeredRowIds = new Set<Uuid>();
  for (const [rowId, i] of bankItemByRow) if (i.decided?.length) answeredRowIds.add(rowId);

  interface Pair {
    item: InboxItem;
    cand: RowCandidate;
  }
  const perItem = new Map<Uuid, RowCandidate[]>();
  const pairs: Pair[] = [];
  for (const item of receipts) {
    if (isDecided(item, 'match')) continue;
    const basis = matchBasis(item);
    if (!basis) continue;
    const rejected = new Set(item.rejectedMatches ?? []);
    const ranked = rankBankRowsForReceipt(
      {
        amountOre: basis.amountOre,
        date: basis.date,
        merchant: basis.counterparty,
        currency: item.reading?.currency || 'SEK',
        direction: directionOf(basis.kind),
        allowedSourceIds: bankSourcesFor(basis.paymentSourceId ?? item.proposal?.paymentSourceId ?? null, ctx.sourceMap),
      },
      pool.filter((r) => !rejected.has(r.id)),
    );
    if (!ranked.length) continue;
    perItem.set(item.id, ranked);
    for (const cand of ranked) pairs.push({ item, cand });
  }
  pairs.sort((a, b) => b.cand.score - a.cand.score || byCreated(a.item, b.item) || byRowDate(a.cand.row, b.cand.row));
  const assigned = new Map<Uuid, RowCandidate>();
  const undecided = new Set<Uuid>();
  const taken = new Set<Uuid>();
  for (const { item, cand } of pairs) {
    if (assigned.has(item.id) || undecided.has(item.id) || taken.has(cand.row.id)) continue;
    const remaining = (perItem.get(item.id) ?? []).filter((c) => !taken.has(c.row.id));
    const [top, second] = remaining;
    if (!top) continue;
    // A clear winner, or a tie between rows that cannot be told apart (the
    // same amount on the same day from the same account: two coffees), where
    // asking would be a question nobody can answer.
    const clear = top.score >= AUTO_LINK_MIN_SCORE && (!second || second.score <= top.score - AUTO_LINK_MIN_GAP || remaining.every((c) => interchangeable(c.row, top.row)));
    if (clear && !answeredRowIds.has(top.row.id)) {
      assigned.set(item.id, top);
      taken.add(top.row.id);
    } else {
      undecided.add(item.id);
    }
  }
  const asksFor = (itemId: Uuid): RowCandidate[] => (perItem.get(itemId) ?? []).filter((c) => !taken.has(c.row.id)).slice(0, 3);

  // Rows claimed by a receipt have no item of their own; every other open row has one.
  const claimed = new Set<Uuid>([...reserved.keys(), ...taken]);
  const itemsByRow = new Set(items.filter((i) => i.kind === 'bank_row' && i.bankRowId).map((i) => i.bankRowId!));
  const missingItems = openRows.filter((r) => !claimed.has(r.id) && !itemsByRow.has(r.id)).map((r) => r.id);
  const created = await ensureBankRowItems(db, companyId, missingItems);
  const retire = [...claimed].filter((id) => {
    const i = bankItemByRow.get(id);
    return i !== undefined && i.status !== 'booked';
  });
  if (retire.length) await retireBankRowItems(db, retire);
  result.changed += created.length + retire.length;

  // The merge sees other receipts with the rows this pass gives them, so a
  // look-alike with a payment of its own is not a duplicate.
  const projected = items.map((i) => {
    const a = assigned.get(i.id);
    if (a) return { ...i, matchedBankRowIds: [a.row.id] };
    if (i.kind === 'receipt' && receiptIds.has(i.id) && !isDecided(i, 'match')) return { ...i, matchedBankRowIds: [] };
    return i;
  });
  const refundTargets = refundablePurchases({ verifikationer, paymentSources: ctx.paymentSources }, { paidFromBank: false });
  const documentedIds = documentedIdsOf(items);

  for (const item of receipts) {
    const extraction = extractionOf(item)!;
    const merchant = (isDecided(item, 'counterparty') ? item.proposal?.counterparty : null) ?? item.reading?.merchant ?? '';
    const a = assigned.get(item.id);
    const confirmed = isDecided(item, 'match');
    const openBankRows = confirmed
      ? item.matchedBankRowIds.map((id) => rowById.get(id)).filter((r): r is BankRow => !!r)
      : a
        ? [a.row]
        : asksFor(item.id).map((c) => c.row);
    // A look-alike is the duplicate of the earlier item, never the other way
    // round, so exactly one of an identical pair is held back.
    const earlier = projected.filter((i) => i.id !== item.id && (i.kind !== 'receipt' || byCreated(i, item) < 0));
    const processed = mergeReceipt(item, readResultOf(item, extraction), ctx, {
      rule: ruleByKey.get(merchantKeyOf(merchant)),
      openBankRows,
      autoMatch: confirmed || !!a,
      duplicates: { verifikationer, items: earlier, documentedIds },
      ...(item.decided?.length && item.proposal ? { decided: { fields: item.decided, proposal: item.proposal, matchedBankRowIds: item.matchedBankRowIds } } : {}),
      refundTargets,
      rejectedRowIds: new Set(item.rejectedMatches ?? []),
      today,
    });
    const extra: ItemPatch = { error: undefined, errorTechnical: undefined };
    if (!item.extraction) extra.extraction = extraction;
    const patch = diffProcessed(item, processed, extra);
    // Only a real change clears an old error: an untouched item keeps its story.
    if (Object.keys(patch).every((k) => k === 'error' || k === 'errorTechnical')) {
      delete patch.error;
      delete patch.errorTechnical;
    }
    await write(item, patch);
    if (processed.status === 'booked' && processed.verifikationId && item.status !== 'booked') {
      result.linkedDocuments.push({ itemId: item.id, verifikationId: processed.verifikationId });
    }
  }

  // ---- Bank rows: what the bookkeeping expects, allocated in date order -----------

  const inboxWithV = items.filter((i) => !!i.verifikationId);
  let openItems = deriveOpenItems({ verifikationer, bankRows: rows, paymentSources: ctx.paymentSources, foreignCurrencyIds: foreignCurrencyIdsFrom(inboxWithV) });
  const refundables = refundablePurchases({ verifikationer, paymentSources: ctx.paymentSources }, { paidFromBank: true });
  const bankItems = [...items.filter((i) => i.kind === 'bank_row' && workable(i) && !claimed.has(i.bankRowId ?? '')), ...created].sort((x, y) => {
    const a = x.bankRowId ? rowById.get(x.bankRowId) : undefined;
    const b = y.bankRowId ? rowById.get(y.bankRowId) : undefined;
    return a && b ? byRowDate(a, b) : byCreated(x, y);
  });
  // Confirmed allocations come off the table before anything is proposed.
  for (const item of bankItems) {
    if (isDecided(item, 'allocations') && item.proposal?.allocations?.length) openItems = consume(openItems, item.proposal.allocations);
  }
  for (const item of bankItems) {
    const row = item.bankRowId ? rowById.get(item.bankRowId) : undefined;
    if (!row) {
      await write(item, diffProcessed(item, { status: 'error', tierReasons: [], flags: [], matchedBankRowIds: [] }, { error: BANK_ROW_MISSING }));
      continue;
    }
    if (row.status === 'open' && item.decided?.length) continue; // in the user's hands
    const merge = mergeBankRow(item, ctx, {
      row,
      openItems,
      siblingRows: rows,
      rule: ruleByKey.get(merchantKeyOf(row.text)),
      classification: classificationOf(item),
      verifikationById,
      refundables,
      rejected: new Set(item.rejectedMatches ?? []),
    });
    if (merge.kind === 'link') {
      await linkRow(db, row, merge.verifikationId, item.id);
      row.status = 'linked';
      openItems = openItems.filter((o) => o.verifikationId !== merge.verifikationId);
      await write(item, diffProcessed(item, merge.processed, { error: undefined }));
      continue;
    }
    const processed = merge.processed;
    const allocations = processed.proposal?.allocations;
    // A proposed settlement or refund takes its share off the table; the
    // candidate a match card merely asks about stays open to other rows.
    const claims = processed.proposal?.kind === 'settle' || processed.proposal?.kind === 'refund';
    if (claims && allocations?.length) openItems = consume(openItems, allocations);
    const needsAi = processed.status !== 'booked' && processed.proposal?.reason === null && !item.classification;
    if (needsAi) result.needsClassification.push(item.id);
    const held = needsAi && opts.holdForClassification === true;
    const patch = diffProcessed(item, held ? { ...processed, status: 'processing' } : processed, { error: undefined, errorTechnical: undefined });
    if (Object.keys(patch).every((k) => k === 'error' || k === 'errorTechnical')) {
      delete patch.error;
      delete patch.errorTechnical;
    }
    await write(item, patch);
  }
  return result;
}

/** For tests and the summary: what the pass would treat as workable. */
export function isWorkableStatus(status: InboxStatus): boolean {
  return WORKABLE.has(status);
}
